import React from "react";
import { Modal } from "@mrwa/rime-replacement/esriControls/general/Dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolderOpen,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  ApplicationMessage,
  messageState,
} from "@mrwa/rime-replacement/esriControls/general/ApplicationMessage";
import NomadSelect from "./NomadSelect";
import { extractGoPro, extractBlackVue } from "./extractions";
import nomadImage from "./Nomad.png";
import { FileType } from "../data/fileType";
import { AuthContext } from "../context";

import "./openFileDialog.css";

// Every manufacturer appears to have an identifying signature in the first few bytes of a video.
// These have been taken from the method used in VideoTripper
// Update the rendered instructions if this list changes
const ManufacturerSignatures = {
  GoPro: "47 50 52 4f", //bytes 28 to 31
  BlackVue: "6D 70 34 32 6D 70 34 32 00 04 B1 25 66 72 65 65", //h.264
};

const UnsupportedSignatures = {
  BlackVue: "6d 70 34 32 6d 70 34 32 00 04 b1 25 66 72 65 65", //h.265
};

// Update the rendered instructions if this list changes
export const AcceptedGPSTypes = ["NMEA", "GPX", "LOG"];

export default class OpenOrExtractFileDialog extends React.Component {
  static contextType = AuthContext;
  constructor(props) {
    super(props);

    this.state = {
      extracting: false,
      nomadSelect: props.loadNomad != null,
      extractionState: "",
      extractionPercent: 0,
    };

    this.mainFileInputClicked = this.mainFileInputClicked.bind(this);
    this.filesSelected = this.filesSelected.bind(this);
    this.nomadFilesSelected = this.nomadFilesSelected.bind(this);

    this.extractFileInputClicked = this.extractFileInputClicked.bind(this);
    this.validateExtractFile = this.validateExtractFile.bind(this);
    this.nomadSelectClicked = this.nomadSelectClicked.bind(this);

    this.mainFileInput = React.createRef();
    this.extractFileInput = React.createRef();
  }

  mainFileInputClicked() {
    this.mainFileInput.current.click();
  }

  extractFileInputClicked() {
    this.extractFileInput.current.click();
  }

  nomadSelectClicked() {
    this.setState({ nomadSelect: true });
  }

  filesSelected(e) {
    const isGps = (type) => {
      const result = AcceptedGPSTypes.some((extension) =>
        type.toUpperCase().endsWith(extension)
      );
      return result;
    };
    const files = Array.from(e.target.files);
    const videoFiles = files
      .filter((file) => !isGps(file.name))
      .map((file) => FileType.FileSystem(file));
    const gpsFiles = files
      .filter((file) => isGps(file.name))
      .map((file) => FileType.FileSystem(file));

    if (gpsFiles.length !== videoFiles.length) {
      messageState.message = ApplicationMessage.Error(
        "Video and GPS file counts do not match"
      );
    } else {
      this.props.filesSelected(videoFiles, gpsFiles);
      this.mainFileInput.current.value = null;
    }
  }

  nomadFilesSelected(files) {
    if (files.length > 0) {
      this.props.filesSelected(
        files.map(({ video }) => FileType.Url(video.url, video.name)),
        files.map(({ gps }) => FileType.Url(gps.url, gps.name)),
        files.map(({ video, gps }) => ({ video: video.name, gps: gps.name }))
      );
    }
    this.setState({ nomadSelect: false });
  }

  validateExtractFile(e) {
    if (e.target.files.length !== 1) return;

    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const fileSignature = [...new Uint8Array(reader.result)]
        .map((x) => x.toString(16).padStart(2, "0"))
        .join(" ");
      const match = Object.entries(ManufacturerSignatures)
        .filter(([_, value]) => fileSignature.includes(value))
        .map(([_, value]) => value)[0];

      switch (match) {
        case ManufacturerSignatures.GoPro:
          this.processGoPro(file);
          break;
        case ManufacturerSignatures.BlackVue:
          this.processBlackVue(file);
          break;
        default:
          const matchUnsupported = Object.entries(UnsupportedSignatures)
            .filter(([_, value]) => fileSignature.includes(value))
            .map(([_, value]) => value)[0];
          switch (matchUnsupported) {
            case UnsupportedSignatures.BlackVue:
              messageState.message = ApplicationMessage.Confirm(
                "The video you have selected is from a BlackVue camera with H.265 (HEVC) enabled. Browsers supported by Road View currently do not support the H.265 (HEVC) codec. Road View can extract GPS data for this video, but the video itself will not be able to be viewed in Road View. If your camera supports recording to H.264 (AVC), enable this codec instead.",
                () => this.processBlackVue(file, true),
                "Invalid Codec Detected"
              );
              break;
            default:
              const validVideoManufactuers = Object.keys(
                ManufacturerSignatures
              ).join("/");
              messageState.message = ApplicationMessage.Error(
                `Unrecognised video type. This tool can only extract from ${validVideoManufactuers} videos`
              );
              break;
          }
      }
    };
    reader.readAsArrayBuffer(file.slice(8, 32));
  }

  async processGoPro(file) {
    this.setState({ extracting: true });
    this.cancellationToken = { cancelled: false };
    try {
      await extractGoPro(
        file,
        (progress) => this.setState(progress),
        this.cancellationToken
      );
    } catch (e) {
      messageState.message = ApplicationMessage.Error(e.message);
    } finally {
      this.setState({ extracting: false });
    }
  }

  async processBlackVue(file, isHEVC = false) {
    this.setState({ extracting: true });
    this.cancellationToken = { cancelled: false };
    try {
      await extractBlackVue(
        file,
        (progress) => this.setState(progress),
        this.cancellationToken,
        isHEVC
      );
    } catch (e) {
      messageState.message = ApplicationMessage.Error(e.message);
    } finally {
      this.setState({ extracting: false });
    }
  }

  close() {
    if (this.cancellationToken) this.cancellationToken.cancelled = true;
    this.setState({ extracting: false, nomadSelect: false });
    this.props.onReject();
  }

  renderExtract() {
    const footerContents = (
      <>
        <button
          type="button"
          className="button light"
          onClick={() => (this.cancellationToken.cancelled = true)}
        >
          Cancel
        </button>
      </>
    );

    const modalProps = {
      show: this.props.show,
      onReject: this.close.bind(this),
      panelClass: "openFileDialog",
      contentsClass: "contents",
      title: this.state.extractionState,
      footerContents,
    };

    return (
      <Modal {...modalProps}>
        <div>
          <progress value={this.state.extractionPercent} max="100">
            {this.state.extractionPercent}%
          </progress>
        </div>
      </Modal>
    );
  }

  renderNomadSelect() {
    if (!this.context.isCorp) return this.renderStandard();

    const footerContents = (
      <>
        {(!this.props.loadNomad &&
          this.state.buttonOverrides &&
          this.state.buttonOverrides.cancel) || (
          <button
            type="button"
            className="button light"
            onClick={() => {
              if (this.props.loadNomad) this.close();
              else this.setState({ nomadSelect: false, buttonOverrides: null });
            }}
          >
            Cancel
          </button>
        )}
        {this.state.buttonOverrides && this.state.buttonOverrides.action}
      </>
    );

    const modalProps = {
      show: this.props.show,
      onReject: this.close.bind(this),
      panelClass: "openFileDialog",
      contentsClass: "contents",
      title: this.state.nomadSelectTitle,
      footerContents,
    };

    return (
      <Modal {...modalProps}>
        <NomadSelect
          setTitle={(title) => this.setState({ nomadSelectTitle: title })}
          filesSelected={this.nomadFilesSelected}
          setButtonOverrides={(buttons) =>
            this.setState({ buttonOverrides: buttons })
          }
          loadNomad={this.props.loadNomad}
          nomadApiUrl={this.props.nomadApiUrl}
        />
      </Modal>
    );
  }

  renderStandard() {
    const footerContents = (
      <>
        <button
          type="button"
          className="button icon-right"
          onClick={this.mainFileInputClicked}
          title="Select files for playing"
        >
          Load
          <FontAwesomeIcon icon={faFolderOpen} size="lg" />
        </button>
        <input
          type="file"
          accept={["video/*", ...AcceptedGPSTypes.map((x) => `.${x}`)].join(
            ", "
          )}
          multiple
          onChange={this.filesSelected}
          style={{ display: "none" }}
          ref={this.mainFileInput}
        ></input>
        <button
          type="button"
          className="button icon-right"
          onClick={this.extractFileInputClicked}
          title="Select video to extract GPS"
        >
          Extract
          <FontAwesomeIcon icon={faMapMarkerAlt} size="lg" />
        </button>
        {this.context.isCorp && (
          <button
            type="button"
            className="button icon-right"
            onClick={this.nomadSelectClicked}
            title="Select video from Nomad for playing"
          >
            Nomad
            <img src={nomadImage} alt="" />
          </button>
        )}
        <input
          type="file"
          accept="video/*"
          onChange={this.validateExtractFile}
          style={{ display: "none" }}
          ref={this.extractFileInput}
        ></input>
      </>
    );

    const modalProps = {
      show: this.props.show,
      onReject: this.props.onReject,
      panelClass: "openFileDialog",
      contentsClass: "contents",
      title: "Select Files",
      footerContents,
    };

    return (
      <Modal {...modalProps}>
        <div className="information">
          <p>
            Road View can load one or many video files with associated GPS
            metadata files. GPS file types that are supported are:
          </p>
          <ul>
            <li>
              <strong>.NMEA</strong> e.g. from Navman cameras
            </li>
            <li>
              <strong>.GPX</strong> e.g. from Garmin cameras
            </li>
            <li>
              <strong>.LOG</strong> containing NMEA data, e.g. from select Sony
              cameras
            </li>
          </ul>
          <p>
            In addition, Road View can extract GPS metadata into a .GPX file
            from videos by the following manufacturers:
          </p>
          <ul>
            <li>
              <strong>GoPro</strong> (Hero5 and later)
            </li>
            <li>
              <strong>BlackVue</strong> (Models that support embedding GPS data
              and H.264 video encoding)
            </li>
          </ul>
          <p>
            The .GPX file will be downloaded automatically by your browser once
            extraction is complete.
          </p>
          <p>
            <i>
              It is strongly suggested that files for extraction are stored on a
              local drive, rather than a network location, for faster processing
              speed.
            </i>
          </p>
        </div>
      </Modal>
    );
  }

  render() {
    if (this.state.extracting) return this.renderExtract();
    if (this.state.nomadSelect) return this.renderNomadSelect();
    return this.renderStandard();
  }
}
